import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import api from "../../services/api.js";
import { assetUrl } from "../../utils/assetUrl.js";

const DISMISS_PREFIX = "coonaltragas_home_popup_dismissed_";

function dismissStorageKey(popup) {
  const id = popup?.id ?? "";
  const ver = popup?.fecha_actualizacion ?? "";
  return `${DISMISS_PREFIX}${id}_${ver}`;
}

function isDismissed(popup) {
  try {
    return sessionStorage.getItem(dismissStorageKey(popup)) === "1";
  } catch {
    return false;
  }
}

function dismissPopup(popup) {
  try {
    sessionStorage.setItem(dismissStorageKey(popup), "1");
  } catch {
    /* ignore */
  }
}

export default function HomeEventPopup() {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/v1/public/home-popup");
        if (cancelled || !data.success || !data.data?.id || !data.data?.imagen)
          return;
        if (isDismissed(data.data)) return;
        setPopup(data.data);
        setImageError(false);
        setVisible(true);
      } catch {
        /* sin aviso activo */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const close = useCallback(() => {
    if (popup) dismissPopup(popup);
    setVisible(false);
  }, [popup]);

  useEffect(() => {
    if (!visible) return undefined;

    function onKeyDown(e) {
      if (e.key === "Escape") close();
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [visible, close]);

  if (!visible || !popup) return null;

  const imageSrc = assetUrl(popup.imagen);
  const link = popup.enlace?.trim();

  const image = imageError ? (
    <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-900">
      No se pudo cargar la imagen del aviso. Intenta recargar la página.
    </p>
  ) : (
    <img
      src={imageSrc}
      alt={popup.titulo || "Evento importante"}
      className="max-h-[min(85vh,920px)] w-full object-contain"
      onError={() => setImageError(true)}
    />
  );

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
      style={{ width: "100vw", height: "100dvh" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="home-popup-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        aria-label="Cerrar aviso"
        onClick={close}
      />
      <div className="relative z-10 w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl ring-1 ring-slate-200/80 sm:max-w-4xl lg:max-w-5xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
          <p
            id="home-popup-title"
            className="truncate font-display text-sm font-semibold text-accent-blue sm:text-base"
          >
            {popup.titulo || "Evento importante"}
          </p>
          <button
            type="button"
            onClick={close}
            className="shrink-0 rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="bg-slate-50 p-4 sm:p-5">
          {link ? (
            <a
              href={link}
              target={link.startsWith("/") ? undefined : "_blank"}
              rel={link.startsWith("/") ? undefined : "noopener noreferrer"}
              className="block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-[#1acb12] focus-visible:ring-offset-2"
              onClick={close}
            >
              {image}
            </a>
          ) : (
            image
          )}
        </div>
        <div className="border-t border-slate-100 px-4 py-3 text-center sm:px-5">
          <button
            type="button"
            onClick={close}
            className="text-sm font-semibold text-slate-800 transition hover:text-accent-blue"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
